import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import StatusBadge from "@/components/StatusBadge";
import POTimeline from "@/components/POTimeline";
import { POLineItemsDetail } from "@/components/POLineItemTable";
import { fmtDate, fmtDateTime, fmtINR } from "@/lib/format";
import {
  ArrowLeft, BellRing, Mail, MessageCircle, Link2, Check, X, Truck, MessageSquare, Send, Ban, CheckCircle2, FileDown,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const CHANNEL_ICON = { whatsapp: MessageCircle, email: Mail, platform: MessageSquare };

export default function PODetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [docs, setDocs] = useState([]);
  const [comment, setComment] = useState("");
  const [channel, setChannel] = useState("whatsapp");
  const [template, setTemplate] = useState("delivery_update_reminder");
  const [sending, setSending] = useState(false);

  const load = useCallback(() => {
    api.get(`/pos/${id}`).then((r) => setData(r.data)).catch(() => toast.error("PO not found"));
    api.get(`/pos/${id}/documents`).then((r) => setDocs(r.data.documents)).catch(() => {});
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="py-20 text-center text-sm text-muted-foreground">Loading PO…</div>;
  const { po, events, communications, modifications } = data;
  const pendingMod = modifications.find((m) => m.status === "pending");

  const sendReminder = async () => {
    setSending(true);
    try {
      await api.post(`/pos/${id}/reminder`, { channel, template_key: template });
      toast.success("Reminder logged successfully");
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSending(false); }
  };

  const quickReminder = async (templateKey) => {
    setSending(true);
    try {
      await api.post(`/pos/${id}/reminder`, { channel: "whatsapp", template_key: templateKey });
      toast.success("Reminder logged successfully");
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSending(false); }
  };

  const copyActionLink = async () => {
    try {
      const r = await api.post(`/pos/${id}/action-link`);
      await navigator.clipboard.writeText(r.data.url);
      toast.success("Secure action link copied (expires in 72h)");
    } catch (e) { toast.error("Could not generate link"); }
  };

  const postComment = async () => {
    if (!comment.trim()) return;
    try {
      await api.post(`/pos/${id}/comments`, { message: comment.trim() });
      setComment("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const resolveMod = async (modId, action) => {
    try {
      await api.post(`/pos/${id}/modifications/${modId}/resolve`, { action });
      toast.success(`Modification ${action}d — supplier notified`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const setStatus = async (status) => {
    try {
      await api.post(`/pos/${id}/status`, { status });
      toast.success(`PO marked ${status}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const info = [
    ["Supplier", po.supplier_name], ["Contact", `${po.supplier_contact} · ${po.supplier_phone}`],
    ["PO Date", fmtDate(po.po_date)], ["PO Value", fmtINR(po.value)],
    ["Buyer / Owner", po.buyer_name], ["Delivery Location", po.delivery_location || "—"],
  ];
  const delivery = [
    ["Manufacturing Status", po.manufacturing_status || "—"],
    ["Dispatch Date", fmtDate(po.dispatch_date)],
    ["Transport Mode", po.transport_mode ? po.transport_mode[0].toUpperCase() + po.transport_mode.slice(1) : "—"],
    ["Tracking / LR Ref", po.tracking_ref || "—"],
    ["Required Delivery", fmtDate(po.required_delivery_date)],
    ["Expected Delivery", fmtDate(po.expected_delivery_date)],
    ["Actual Delivery", fmtDate(po.actual_delivery_date)],
    ["Delay", po.delay_days > 0 ? `${po.delay_days} days` : "None"],
  ];

  return (
    <div data-testid="po-detail-page" className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="ghost" size="icon" data-testid="back-btn" onClick={() => navigate(-1)}><ArrowLeft className="h-4 w-4" /></Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="font-mono font-heading text-2xl font-semibold tracking-tight" data-testid="po-number">{po.po_number}</h1>
            <StatusBadge status={po.status} />
            {po.modification_pending && <span data-testid="mod-pending-flag" className="rounded-full bg-[#FEF3C7] border border-[#FDE68A] px-2.5 py-0.5 text-xs font-semibold text-[#92400E]">Modification pending</span>}
          </div>
          <p className="text-sm text-muted-foreground">{po.item} · {po.quantity} {po.uom} · {po.supplier_name}</p>
        </div>
        <Button variant="outline" data-testid="copy-action-link-btn" onClick={copyActionLink}><Link2 className="mr-1.5 h-4 w-4" /> Supplier Action Link</Button>
        {po.status !== "closed" && po.status !== "cancelled" && (
          <>
            <Button variant="outline" data-testid="close-po-btn" onClick={() => setStatus("closed")}><CheckCircle2 className="mr-1.5 h-4 w-4" /> Close</Button>
            <Button variant="outline" data-testid="cancel-po-btn" className="text-red-700 border-red-200 hover:bg-red-50" onClick={() => setStatus("cancelled")}><Ban className="mr-1.5 h-4 w-4" /> Cancel</Button>
          </>
        )}
      </div>

      <div className="rounded-lg border bg-card">
        <h2 className="border-b px-5 py-3 font-heading text-sm font-semibold">PO Line Items</h2>
        <POLineItemsDetail po={po} />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="rounded-lg border bg-card">
              <h2 className="border-b px-5 py-3 font-heading text-sm font-semibold">PO Information</h2>
              <dl className="divide-y px-5">
                {info.map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-4 py-2.5 text-sm">
                    <dt className="text-muted-foreground">{k}</dt><dd className="text-right font-medium">{v}</dd>
                  </div>
                ))}
              </dl>
            </div>
            <div className="rounded-lg border bg-card">
              <h2 className="flex items-center gap-2 border-b px-5 py-3 font-heading text-sm font-semibold"><Truck className="h-4 w-4" /> Delivery Tracking</h2>
              <dl className="divide-y px-5">
                {delivery.map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-4 py-2 text-sm">
                    <dt className="text-muted-foreground">{k}</dt>
                    <dd className={`text-right font-medium ${k === "Delay" && po.delay_days > 0 ? "text-[#DC2626]" : ""}`}>{v}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>

          {pendingMod && (
            <div data-testid="modification-request" className="rounded-lg border border-[#FDE68A] bg-[#FEF3C7]/50 p-5">
              <h2 className="font-heading text-sm font-semibold text-[#92400E]">Delivery Modification Request</h2>
              <div className="mt-3 grid grid-cols-3 gap-4 text-sm">
                <div><p className="text-xs text-muted-foreground">Current Date</p><p className="font-semibold">{fmtDate(pendingMod.current_date)}</p></div>
                <div><p className="text-xs text-muted-foreground">Requested Date</p><p className="font-semibold text-[#92400E]">{fmtDate(pendingMod.requested_date)}</p></div>
                <div><p className="text-xs text-muted-foreground">Reason</p><p className="font-medium">{pendingMod.reason}</p></div>
              </div>
              <div className="mt-4 flex gap-2">
                <Button size="sm" data-testid="mod-approve-btn" onClick={() => resolveMod(pendingMod.id, "approve")} className="bg-[#166534] hover:bg-[#14532D]"><Check className="mr-1 h-4 w-4" /> Approve</Button>
                <Button size="sm" variant="outline" data-testid="mod-reject-btn" onClick={() => resolveMod(pendingMod.id, "reject")} className="border-red-200 text-red-700 hover:bg-red-50"><X className="mr-1 h-4 w-4" /> Reject</Button>
                <Button size="sm" variant="outline" data-testid="mod-discuss-btn" onClick={() => document.getElementById("comment-box")?.focus()}>Discuss</Button>
              </div>
            </div>
          )}

          <div className="rounded-lg border bg-card">
            <h2 className="border-b px-5 py-3 font-heading text-sm font-semibold">Buyer Actions & Reminders</h2>
            <div className="flex flex-wrap gap-2 border-b px-5 py-3" data-testid="quick-actions">
              <Button size="sm" variant="outline" data-testid="req-ack-btn" disabled={sending} onClick={() => quickReminder("ack_reminder")}>Request PO Acknowledgement</Button>
              <Button size="sm" variant="outline" data-testid="req-update-btn" disabled={sending} onClick={() => quickReminder("delivery_update_reminder")}>Request Delivery Update</Button>
              <Button size="sm" variant="outline" data-testid="req-confirm-btn" disabled={sending} onClick={() => quickReminder("delivery_date_confirmation")}>Request Delivery Date Confirmation</Button>
            </div>
            <div className="flex flex-wrap items-end gap-3 p-5">
              <div>
                <p className="mb-1.5 text-xs font-medium text-muted-foreground">Channel</p>
                <Select value={channel} onValueChange={setChannel}>
                  <SelectTrigger data-testid="reminder-channel" className="w-44"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="whatsapp">WhatsApp</SelectItem>
                    <SelectItem value="email">Email</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="min-w-64 flex-1">
                <p className="mb-1.5 text-xs font-medium text-muted-foreground">Template</p>
                <Select value={template} onValueChange={setTemplate}>
                  <SelectTrigger data-testid="reminder-template"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="ack_reminder">PO Acknowledgement Reminder</SelectItem>
                    <SelectItem value="delivery_update_reminder">Delivery Update Reminder</SelectItem>
                    <SelectItem value="delivery_date_confirmation">Delivery Date Confirmation</SelectItem>
                    <SelectItem value="delay_alert">Delay Alert</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button data-testid="send-reminder-btn" disabled={sending} onClick={sendReminder} className="bg-slate-900 hover:bg-slate-800">
                <BellRing className="mr-1.5 h-4 w-4" /> {channel === "whatsapp" ? "Send WhatsApp Reminder" : "Send Email Reminder"}
              </Button>
            </div>
            <p className="px-5 pb-4 text-xs text-muted-foreground">Messages are simulated & logged until a WhatsApp Business API / email provider is connected in Settings → Integrations.</p>
          </div>

          <div className="rounded-lg border bg-card">
            <h2 className="border-b px-5 py-3 font-heading text-sm font-semibold">Documents</h2>
            <div className="divide-y" data-testid="po-documents">
              {docs.length === 0 && <p className="p-5 text-sm text-muted-foreground">No documents uploaded yet. Suppliers can attach invoices, dispatch and transport documents from their portal.</p>}
              {docs.map((d) => (
                <div key={d.id} data-testid={`doc-${d.id.slice(0, 8)}`} className="flex items-center gap-3 px-5 py-3 text-sm">
                  <FileDown className="h-4 w-4 shrink-0 text-slate-400" />
                  <div className="min-w-0 flex-1">
                    <p className="font-medium">{d.filename}</p>
                    <p className="text-xs text-muted-foreground capitalize">{d.doc_type} · {d.uploaded_by} · <span className="font-mono">{fmtDateTime(d.created_at)}</span></p>
                  </div>
                  <a href={`${process.env.REACT_APP_BACKEND_URL}/api/documents/${d.id}/download`} target="_blank" rel="noreferrer">
                    <Button size="sm" variant="outline" data-testid={`doc-download-${d.id.slice(0, 8)}`}>Download</Button>
                  </a>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-lg border bg-card">
            <h2 className="border-b px-5 py-3 font-heading text-sm font-semibold">Communication Center</h2>
            <div className="max-h-96 space-y-4 overflow-y-auto p-5" data-testid="communication-history">
              {communications.length === 0 && <p className="text-sm text-muted-foreground">No communication yet.</p>}
              {[...communications].reverse().map((c) => {
                const Icon = CHANNEL_ICON[c.channel] || MessageSquare;
                const isSystem = c.direction === "system";
                const fromBuyer = c.direction === "buyer_to_supplier";
                return (
                  <div key={c.id} data-testid={`comm-${c.id.slice(0, 8)}`} className={`flex gap-3 ${fromBuyer ? "flex-row-reverse" : ""}`}>
                    <div className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border ${c.channel === "whatsapp" ? "bg-green-50 text-green-700" : c.channel === "email" ? "bg-blue-50 text-blue-700" : "bg-slate-100 text-slate-600"}`}>
                      <Icon className="h-3.5 w-3.5" />
                    </div>
                    <div className={`max-w-[75%] rounded-lg border px-3.5 py-2.5 ${isSystem ? "bg-slate-50" : fromBuyer ? "bg-slate-900 text-white" : "bg-white"}`}>
                      <p className={`text-[11px] font-medium ${fromBuyer && !isSystem ? "text-slate-300" : "text-muted-foreground"}`}>
                        {c.sender_name} · {fromBuyer ? "Buyer → Supplier" : isSystem ? "System" : "Supplier → Platform"}
                      </p>
                      <p className="mt-1 whitespace-pre-wrap text-sm">{c.message}</p>
                      <p className={`mt-1 font-mono text-[10px] ${fromBuyer && !isSystem ? "text-slate-400" : "text-slate-400"}`}>
                        {fmtDateTime(c.created_at)}{c.delivery_status === "simulated" ? " · simulated" : ""}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex gap-2 border-t p-4">
              <Textarea id="comment-box" data-testid="comment-input" value={comment} onChange={(e) => setComment(e.target.value)}
                placeholder="Write a message to the supplier… (recorded on the PO timeline)" className="min-h-10 resize-none" rows={1} />
              <Button data-testid="comment-send-btn" onClick={postComment} className="bg-slate-900 hover:bg-slate-800"><Send className="h-4 w-4" /></Button>
            </div>
          </div>
        </div>

        <div className="lg:sticky lg:top-20 lg:self-start">
          <div className="rounded-lg border bg-card">
            <h2 className="border-b px-5 py-3 font-heading text-sm font-semibold">PO Timeline — Audit Trail</h2>
            <div className="max-h-[70vh] overflow-y-auto p-5">
              <POTimeline events={events} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
